import { useSearchParams } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import { SyntheticEvent, useEffect, useState } from 'react'

import { If } from '@src/quince-components/src/atoms/conditionals'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'
import { getStorage } from '@src/utils/enhancedStorage'

import FormHeader from '../../../../../components/Auth/FormHeader'
import Button from '../../../../../components/Button'
import { BoxStyledErrorMessage } from '../../../../../components/FormElements'
import { StyledVerificationMessage } from '../../../../../components/FormElements/StyledVerificationMessage'
import { GoogleSignIn } from '../../../../Auth/GoogleSignIn/GoogleSignIn'
import { ContinueButton } from '../../../../LogIn/Components/ContinueButton'
import { Email } from '../../../../LogIn/Components/Email'
import styles from '../../../../LogIn/Components/loginComponents.module.scss'
import { TnC } from '../../../../LogIn/Components/TnC'
import useForm from '../../../../LogIn/hook/useForm'

import newStyles from './initialPage.module.scss'

interface InitialPagePropTypes {
  serverError?: { message: string }
  unverifiedAccount?: boolean
  resendVerificationEmail: (email?: string) => void
  verificationEmailSent?: boolean
  email?: string
  loading?: boolean
  onPageStateChange: (email: string) => void
  error?: string | null
}

// @i18n-ignore
const RESEND_BUTTON_TRACK_CATEGORY = 'login'
// @i18n-ignore
const LOGIN_FORM_A_INITIAL = 'login-form-A-Initial'

const InitialPage = ({
  serverError,
  unverifiedAccount,
  resendVerificationEmail,
  verificationEmailSent,
  email,
  loading,
  onPageStateChange,
  error,
}: InitialPagePropTypes) => {
  const { t } = useTranslation('common')
  const deviceInfo = useDeviceInfo()
  const [errors, setErrors] = useState({})
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const { email: userEmail, onEmailChange } = useForm({ email })
  const searchParams = useSearchParams()
  const [fromAction, setFromAction] = useState('')
  useEffect(() => {
    setFromAction(getStorage().getItem('fromContactAction', { isGlobal: true }) ?? searchParams?.get('from') ?? '')
  }, [searchParams])
  const onInitialPageSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    let error = ''
    if (!userEmail) {
      error = t('please_fill_in_the_email')
    } else if (!validateEmail(userEmail)) {
      error = t('please_enter_valid_email_address')
    }
    if (error) {
      setErrors({
        username: {
          message: error,
        },
      })
    } else {
      onPageStateChange(userEmail)
    }
  }

  return (
    <form onSubmit={onInitialPageSubmit} id="login-form-A-Initial" noValidate>
      <FormHeader>
        <h1 className={styles.formHeading}>{t('sign_in_register')}</h1>
        {fromAction && <div className={styles.formHeading}>{t('to_manage_your_order')}</div>}
        {!fromAction && <div className={styles.formSubHeading}>{t('free_shipping_365_day_ret_atbvw9')}</div>}
        {fromAction && <div className={styles.formSubHeading}>{t('order_management_signin')}</div>}
      </FormHeader>
      {serverError && (
        <BoxStyledErrorMessage dangerouslySetInnerHTML={{ __html: getSanitizedHtml(serverError.message) }} />
      )}
      {error && <BoxStyledErrorMessage>{error}</BoxStyledErrorMessage>}
      {unverifiedAccount && (
        <StyledVerificationMessage>
          {t('cannot_find_verification_mail')}
          <Button
            onClick={() => {
              resendVerificationEmail(email)
            }}
            trackParams={{
              trackInternal: true,
              category: RESEND_BUTTON_TRACK_CATEGORY,
              action: 'account_login_unverified',
              context: {
                login_email: email ?? '',
                lb_user_id: getlbUserId(),
              },
            }}
            className={styles.unVerifiedMessage}
          >
            {t('resend_one')}
          </Button>
        </StyledVerificationMessage>
      )}
      {verificationEmailSent && <StyledVerificationMessage>{t('verification_e_mail_sent')}</StyledVerificationMessage>}
      <GoogleSignIn />
      <div className={styles.divider}>{t('or')}</div>
      <Email errors={errors} defaultValue={email} value={userEmail} onChange={onEmailChange} />
      <ContinueButton loading={loading} form={LOGIN_FORM_A_INITIAL} />
      <If condition={isDesktop}>
        <div className={newStyles.footer}>
          <TnC />
        </div>
      </If>
    </form>
  )
}

export { InitialPage }
